// Kompas Dnia — localStorage store.
// No backend, no auth. All data lives here.

const KEYS = {
  settings: "dailyCompass.settings",
  checkIns: "dailyCompass.checkIns",
};

const DEFAULT_SETTINGS = {
  language: "pl",
  onboardingCompleted: false,
};

/* ---------- low-level helpers ---------- */

function safeParse(raw, fallback) {
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readLS(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    return safeParse(window.localStorage.getItem(key), fallback);
  } catch {
    return fallback;
  }
}

function writeLS(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / privacy mode — silent fail is fine for Phase 1.
  }
}

/* ---------- settings ---------- */

export function getSettings() {
  const stored = readLS(KEYS.settings, null);
  if (!stored || typeof stored !== "object") {
    return { ...DEFAULT_SETTINGS };
  }
  return { ...DEFAULT_SETTINGS, ...stored };
}

export function saveSettings(settings) {
  const next = { ...getSettings(), ...settings };
  writeLS(KEYS.settings, next);
  return next;
}

export function changeLanguage(lang) {
  if (lang !== "pl" && lang !== "en") return;
  saveSettings({ language: lang });
}

export function markOnboardingCompleted() {
  saveSettings({ onboardingCompleted: true });
}

/* ---------- check-ins (used from Phase 2+, helpers defined now) ---------- */

export function getCheckIns() {
  const stored = readLS(KEYS.checkIns, []);
  return Array.isArray(stored) ? stored : [];
}

export function saveCheckIn(entry) {
  if (!entry || typeof entry !== "object" || !entry.date) return;
  const all = getCheckIns();
  const rest = all.filter((e) => e.date !== entry.date);
  rest.push(entry);
  rest.sort((a, b) => (a.date < b.date ? 1 : -1));
  writeLS(KEYS.checkIns, rest);
}

export function getCheckInByDate(dateISO) {
  return getCheckIns().find((e) => e.date === dateISO) || null;
}

/**
 * Returns the most recently created check-in (sorted by `date` descending),
 * or null if there are no check-ins yet.
 */
export function getLatestCheckIn() {
  const all = getCheckIns();
  return all.length > 0 ? all[0] : null;
}

/**
 * Returns all check-ins sorted from newest to oldest.
 * (Currently an alias for getCheckIns which already sorts desc,
 *  but keeping a named export makes intent clearer at call sites.)
 */
export function getHistory() {
  return getCheckIns();
}

/**
 * Updates the reflectionNote field of the entry with the given date.
 * No-op if no entry exists for that date.
 */
export function updateReflectionNote(dateISO, note) {
  const entry = getCheckInByDate(dateISO);
  if (!entry) return;
  const updated = { ...entry, reflectionNote: String(note || "") };
  saveCheckIn(updated);
}

/* ---------- destructive ---------- */

export function clearAllLocalData() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEYS.settings);
    window.localStorage.removeItem(KEYS.checkIns);
  } catch {
    // ignore
  }
}

export const STORE_KEYS = KEYS;
